import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import "./../../../assets/css/student/roadmap/roadmap1.css";

/* ─── Static Data ──────────────────────────────── */
const WEEKLY_XP = [
  { day: "Mon", xp: 120 },
  { day: "Tue", xp: 200 },
  { day: "Wed", xp: 80 },
  { day: "Thu", xp: 260 },
  { day: "Fri", xp: 180 },
  { day: "Sat", xp: 310 },
  { day: "Sun", xp: 140 },
];

const SKILL_BREAKDOWN = [
  { label: "HTML & Structure", pct: 90 },
  { label: "CSS & Layout", pct: 62 },
  { label: "JavaScript", pct: 38 },
  { label: "React", pct: 15 },
];

const INITIAL_MODULES = [
  {
    id: 1,
    title: "HTML Fundamentals",
    duration: "3 days",
    xp: 150,
    state: "completed",
    progress: 100,
    desc: "Semantic HTML, forms, accessibility basics.",
  },
  {
    id: 2,
    title: "CSS Fundamentals",
    duration: "5 days",
    xp: 200,
    state: "current",
    progress: 48,
    desc: "Box model, Flexbox, Grid, responsive design.",
  },
  {
    id: 3,
    title: "JavaScript Basics",
    duration: "7 days",
    xp: 280,
    state: "locked",
    progress: 0,
    desc: "Variables, functions, DOM manipulation, ES6+.",
  },
  {
    id: 4,
    title: "React Fundamentals",
    duration: "8 days",
    xp: 320,
    state: "locked",
    progress: 0,
    desc: "Components, hooks, state management, routing.",
  },
  {
    id: 5,
    title: "Portfolio Project",
    duration: "6 days",
    xp: 400,
    state: "locked",
    progress: 0,
    desc: "Build & deploy a complete professional portfolio.",
  },
];

/* ─── Sub-components ───────────────────────────── */

function StatBadge({ icon, value, label }) {
  return (
    <div className="rm-stat">
      <span className="rm-stat__icon">{icon}</span>
      <div>
        <div className="rm-stat__value">{value}</div>
        <div className="rm-stat__label">{label}</div>
      </div>
    </div>
  );
}

function ProgressBar({ pct, accent }) {
  return (
    <div className="rm-bar-track">
      <motion.div
        className={`rm-bar-fill ${accent ? "rm-bar-fill--accent" : ""}`}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    </div>
  );
}

function ModuleItem({ module, onMarkComplete, index }) {
  const stateClass = {
    completed: "rm-module--completed",
    current: "rm-module--current",
    locked: "rm-module--locked",
  }[module.state];

  return (
    <motion.div
      className={`rm-module ${stateClass}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08, duration: 0.45 }}
    >
      {/* Timeline spine */}
      <div className="rm-module__spine">
        <div className="rm-module__dot">
          {module.state === "completed" && (
            <span className="rm-module__tick">✓</span>
          )}
          {module.state === "current" && <span className="rm-module__pulse" />}
          {module.state === "locked" && (
            <span className="rm-module__lock">🔒</span>
          )}
        </div>
        {index < INITIAL_MODULES.length - 1 && (
          <div className="rm-module__line" />
        )}
      </div>

      {/* Card */}
      <motion.div
        className="rm-module__card"
        whileHover={
          module.state !== "locked"
            ? { y: -2, boxShadow: "0 10px 30px rgba(120,28,46,0.1)" }
            : {}
        }
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
      >
        <div className="rm-module__top">
          <h3 className="rm-module__title">{module.title}</h3>
          <span className="rm-module__xp">+{module.xp} XP</span>
        </div>
        <p className="rm-module__desc">{module.desc}</p>

        <div className="rm-module__meta">
          <span className="rm-module__duration">⏱ {module.duration}</span>
          {module.state !== "locked" && (
            <span className="rm-module__pct">{module.progress}%</span>
          )}
        </div>

        {module.state !== "locked" && (
          <ProgressBar
            pct={module.progress}
            accent={module.state === "current"}
          />
        )}

        {module.state === "current" && (
          <motion.button
            className="rm-module__btn"
            onClick={() => onMarkComplete(module.id)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
          >
            Mark as Complete ✦
          </motion.button>
        )}

        {module.state === "completed" && (
          <div className="rm-module__done">Completed ✓</div>
        )}
      </motion.div>
    </motion.div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rm-tooltip">
        <div className="rm-tooltip__day">{label}</div>
        <div className="rm-tooltip__xp">{payload[0].value} XP</div>
      </div>
    );
  }
  return null;
};

/* ─── Main Component ───────────────────────────── */
export default function Roadmap({ selections = {}, onBack }) {
  const [modules, setModules] = useState(INITIAL_MODULES);
  const [totalXP, setTotalXP] = useState(1290);
  const [streak] = useState(7);
  const [badges] = useState(4);

  const currentModule = modules.find((m) => m.state === "current");
  const completedCount = modules.filter((m) => m.state === "completed").length;
  const overallPct = Math.round((completedCount / modules.length) * 100);

  const handleMarkComplete = useCallback(
    (id) => {
      setModules((prev) => {
        const idx = prev.findIndex((m) => m.id === id);
        if (idx === -1) return prev;
        return prev.map((m, i) => {
          if (i === idx) return { ...m, state: "completed", progress: 100 };
          if (i === idx + 1) return { ...m, state: "current" };
          return m;
        });
      });
      setTotalXP((xp) => xp + (modules.find((m) => m.id === id)?.xp || 0));
    },
    [modules],
  );

  const levelLabel = {
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
  };
  const goalLabel = {
    job: "Get a Job",
    freelance: "Freelancing",
    basics: "Learn Basics",
  };
  const timeLabel = { "2h": "2 hrs/day", "4h": "4 hrs/day", flex: "Flexible" };

  return (
    <div className="rm-root">
      <div className="rm-bg-texture" />

      <div className="rm-container">
        {/* Nav */}
        <nav className="rm-nav">
          <div className="rm-logo">
            <span className="rm-logo__dot" />
            <span className="rm-logo__name">Pathwise</span>
          </div>
          {onBack && (
            <button className="rm-back-btn" onClick={onBack}>
              ← Back
            </button>
          )}
        </nav>

        {/* Dashboard */}
        <motion.div
          className="rm-dashboard"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <div className="rm-dashboard__left">
            <h1 className="rm-dashboard__title">Your Learning Roadmap</h1>
            <p className="rm-dashboard__sub">
              Keep the momentum — you're doing great.
            </p>
          </div>
          <div className="rm-dashboard__stats">
            <StatBadge icon="🔥" value={`${streak} days`} label="Streak" />
            <StatBadge
              icon="⭐"
              value={totalXP.toLocaleString()}
              label="Total XP"
            />
            <StatBadge icon="🏆" value={badges} label="Badges" />
          </div>
        </motion.div>

        {/* Personalization Banner */}
        {selections && Object.keys(selections).length > 0 && (
          <motion.div
            className="rm-banner"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            <span className="rm-banner__label">
              ✦ Tailored for you based on
            </span>
            <div className="rm-banner__tags">
              {selections.level && (
                <span className="rm-tag">
                  {levelLabel[selections.level] || selections.level}
                </span>
              )}
              {selections.goal && (
                <span className="rm-tag">
                  {goalLabel[selections.goal] || selections.goal}
                </span>
              )}
              {selections.time && (
                <span className="rm-tag">
                  {timeLabel[selections.time] || selections.time}
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* Next Step Card */}
        {currentModule && (
          <motion.div
            className="rm-next-card"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className="rm-next-card__badge">👉 Up Next</div>
            <div className="rm-next-card__body">
              <div>
                <h2 className="rm-next-card__title">{currentModule.title}</h2>
                <p className="rm-next-card__desc">{currentModule.desc}</p>
                <div className="rm-next-card__meta">
                  <span>⏱ {currentModule.duration}</span>
                  <span>+{currentModule.xp} XP</span>
                  <span>{currentModule.progress}% complete</span>
                </div>
                <ProgressBar pct={currentModule.progress} accent />
              </div>
              <motion.button
                className="rm-next-card__btn"
                onClick={() => handleMarkComplete(currentModule.id)}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
              >
                Continue Learning →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Main Layout */}
        <div className="rm-layout">
          {/* Roadmap Timeline */}
          <section className="rm-timeline-section">
            <h2 className="rm-section-title">Learning Path</h2>
            <div className="rm-timeline">
              {modules.map((mod, idx) => (
                <ModuleItem
                  key={mod.id}
                  module={mod}
                  index={idx}
                  onMarkComplete={handleMarkComplete}
                />
              ))}
            </div>
          </section>

          {/* Right Column */}
          <aside className="rm-aside">
            {/* Overall Progress */}
            <motion.div
              className="rm-card"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25, duration: 0.5 }}
            >
              <h3 className="rm-card__title">Overall Progress</h3>
              <div className="rm-overall">
                <div className="rm-overall__ring">
                  <svg viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="34" className="rm-ring-bg" />
                    <motion.circle
                      cx="40"
                      cy="40"
                      r="34"
                      className="rm-ring-fill"
                      strokeDasharray={`${2 * Math.PI * 34}`}
                      strokeDashoffset={`${2 * Math.PI * 34 * (1 - overallPct / 100)}`}
                      initial={{ strokeDashoffset: `${2 * Math.PI * 34}` }}
                      animate={{
                        strokeDashoffset: `${2 * Math.PI * 34 * (1 - overallPct / 100)}`,
                      }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </svg>
                  <div className="rm-ring-label">{overallPct}%</div>
                </div>
                <div className="rm-overall__info">
                  <div className="rm-overall__stat">
                    <span className="rm-overall__num">{completedCount}</span>
                    <span className="rm-overall__sub">Completed</span>
                  </div>
                  <div className="rm-overall__divider" />
                  <div className="rm-overall__stat">
                    <span className="rm-overall__num">
                      {modules.length - completedCount}
                    </span>
                    <span className="rm-overall__sub">Remaining</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Weekly XP Chart */}
            <motion.div
              className="rm-card"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35, duration: 0.5 }}
            >
              <h3 className="rm-card__title">Weekly XP</h3>
              <div className="rm-chart-wrap">
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart
                    data={WEEKLY_XP}
                    barSize={18}
                    margin={{ top: 4, right: 4, left: -24, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="day"
                      tick={{
                        fill: "#6b6b6b",
                        fontSize: 11,
                        fontFamily: "Georgia",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{
                        fill: "#6b6b6b",
                        fontSize: 10,
                        fontFamily: "Georgia",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "rgba(120,28,46,0.05)" }}
                    />
                    <Bar dataKey="xp" radius={[5, 5, 0, 0]}>
                      {WEEKLY_XP.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={
                            entry.day === "Sat"
                              ? "#781c2e"
                              : "rgba(120,28,46,0.18)"
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Skill Breakdown */}
            <motion.div
              className="rm-card"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
            >
              <h3 className="rm-card__title">Skill Breakdown</h3>
              <div className="rm-skills">
                {SKILL_BREAKDOWN.map((s) => (
                  <div key={s.label} className="rm-skill-row">
                    <div className="rm-skill-row__header">
                      <span className="rm-skill-row__label">{s.label}</span>
                      <span className="rm-skill-row__pct">{s.pct}%</span>
                    </div>
                    <ProgressBar pct={s.pct} />
                  </div>
                ))}
              </div>
            </motion.div>
          </aside>
        </div>
      </div>
    </div>
  );
}
